import { useCallback, useMemo, useRef, useState } from 'react';

/**
 * `useTableView` — client-side sorting + "intentional" (named, preset)
 * filtering for admin tables.
 *
 * Sorting is driven by a set of named accessors; filtering by a set of named
 * filters, each of which is a fixed list of named options (e.g. "All / None /
 * At least one") mapping to predicates, with one option marked default. This is
 * deliberately not a generic value filter: the app owns the option labels and
 * their meaning, the hook only owns the mechanics.
 *
 * State can be controlled (`value` + `onChange`) so an app can persist it, for
 * example in URL search params. Left uncontrolled it keeps its own state seeded
 * from `initial`.
 */

export type SortDir = 'asc' | 'desc';

export type SortValue = string | number | Date | null | undefined;

export interface SortConfig<T> {
  /** Extracts the comparable value. `null`/`undefined` always sort last. */
  accessor: (item: T) => SortValue;
  /**
   * Value kind — drives comparison and the first direction picked when the
   * column is newly selected. Defaults to `'string'`.
   */
  type?: 'string' | 'number' | 'date';
  /**
   * Direction applied when this key is first selected. Defaults to `'asc'` for
   * strings and `'desc'` for numbers and dates (most-recent / largest first).
   */
  firstDir?: SortDir;
}

export interface FilterConfig<T> {
  /** Option value → predicate. Rows for which the predicate returns true pass. */
  options: Record<string, (item: T) => boolean>;
  /** Option value applied when the filter is untouched / reset. */
  default: string;
}

export interface SortState {
  key: string;
  dir: SortDir;
}

export interface TableViewState {
  sort?: SortState | null;
  filters?: Record<string, string>;
}

export interface UseTableViewOptions<T> {
  sorts?: Record<string, SortConfig<T>>;
  filters?: Record<string, FilterConfig<T>>;
  /** Controlled state. Provide together with `onChange`. */
  value?: TableViewState;
  onChange?: (next: TableViewState) => void;
  /** Initial state for the uncontrolled case. */
  initial?: TableViewState;
}

export interface UseTableViewResult<T> {
  /** Filtered, then sorted rows. */
  rows: T[];
  /** Current sort, or null when unsorted. */
  sort: SortState | null;
  /** Cycles a column: same key flips asc⇄desc, a new key uses its first dir. */
  toggleSort: (key: string) => void;
  /** Current direction for `key` if it is the active sort, else null. */
  sortDirFor: (key: string) => SortDir | null;
  /** Resolved option value for every configured filter (defaults filled in). */
  filterValues: Record<string, string>;
  /** Sets one filter to a named option value. */
  setFilter: (key: string, value: string) => void;
  /** True when any filter is off its default. */
  isFiltered: boolean;
  /** Resets every filter back to its default option. Leaves sort untouched. */
  reset: () => void;
}

function defaultFirstDir(type: SortConfig<unknown>['type']): SortDir {
  return type === 'number' || type === 'date' ? 'desc' : 'asc';
}

function toTime(value: string | number | Date): number {
  return value instanceof Date ? value.getTime() : new Date(value).getTime();
}

function compare(
  a: SortValue,
  b: SortValue,
  type: SortConfig<unknown>['type'],
): number {
  const aNull = a == null;
  const bNull = b == null;
  // Nulls always last, regardless of direction.
  if (aNull || bNull) return aNull === bNull ? 0 : aNull ? 1 : -1;

  if (type === 'number') return (a as number) - (b as number);
  if (type === 'date') return toTime(a) - toTime(b);
  return String(a).localeCompare(String(b), 'sv', {
    numeric: true,
    sensitivity: 'base',
  });
}

export function useTableView<T>(
  items: T[],
  { sorts, filters, value, onChange, initial }: UseTableViewOptions<T> = {},
): UseTableViewResult<T> {
  const controlled = value !== undefined && onChange !== undefined;
  const [internal, setInternal] = useState<TableViewState>(() => initial ?? {});
  const state = controlled ? (value as TableViewState) : internal;

  // Keep the latest resolved state in a ref so the setter helpers can compute a
  // "next" value without being re-created on every render.
  const stateRef = useRef(state);
  stateRef.current = state;

  const commit = useCallback(
    (next: TableViewState) => {
      if (controlled) onChange?.(next);
      else setInternal(next);
    },
    [controlled, onChange],
  );

  const filterValues = useMemo(() => {
    const resolved: Record<string, string> = {};
    for (const [key, cfg] of Object.entries(filters ?? {})) {
      const current = state.filters?.[key];
      resolved[key] = current && current in cfg.options ? current : cfg.default;
    }
    return resolved;
  }, [filters, state.filters]);

  const toggleSort = useCallback(
    (key: string) => {
      const cfg = sorts?.[key];
      if (!cfg) return;
      const prev = stateRef.current.sort;
      const nextDir: SortDir =
        prev?.key === key
          ? prev.dir === 'asc'
            ? 'desc'
            : 'asc'
          : (cfg.firstDir ?? defaultFirstDir(cfg.type));
      commit({ ...stateRef.current, sort: { key, dir: nextDir } });
    },
    [sorts, commit],
  );

  const setFilter = useCallback(
    (key: string, optionValue: string) => {
      const current = stateRef.current.filters ?? {};
      commit({
        ...stateRef.current,
        filters: { ...current, [key]: optionValue },
      });
    },
    [commit],
  );

  const reset = useCallback(() => {
    commit({ ...stateRef.current, filters: {} });
  }, [commit]);

  const sortDirFor = useCallback(
    (key: string): SortDir | null =>
      state.sort?.key === key ? state.sort.dir : null,
    [state.sort],
  );

  const isFiltered = useMemo(
    () =>
      Object.entries(filters ?? {}).some(
        ([key, cfg]) => filterValues[key] !== cfg.default,
      ),
    [filters, filterValues],
  );

  const rows = useMemo(() => {
    // Filter first (cheaper sort, and matches "act on visible rows"), then sort.
    let out = items;
    for (const [key, cfg] of Object.entries(filters ?? {})) {
      const predicate = cfg.options[filterValues[key]];
      if (predicate) out = out.filter((item) => predicate(item));
    }

    const sort = state.sort;
    const sortCfg = sort ? sorts?.[sort.key] : undefined;
    if (sort && sortCfg) {
      const dir = sort.dir === 'asc' ? 1 : -1;
      // Decorate-sort-undecorate: read each accessor once, not O(n log n) times.
      out = out
        .map((item) => ({ item, key: sortCfg.accessor(item) }))
        .sort((a, b) => {
          const aNull = a.key == null;
          const bNull = b.key == null;
          // Nulls always last, independent of direction.
          if (aNull || bNull) return aNull === bNull ? 0 : aNull ? 1 : -1;
          return compare(a.key, b.key, sortCfg.type) * dir;
        })
        .map(({ item }) => item);
    }

    return out;
  }, [items, filters, filterValues, sorts, state.sort]);

  return {
    rows,
    sort: state.sort ?? null,
    toggleSort,
    sortDirFor,
    filterValues,
    setFilter,
    isFiltered,
    reset,
  };
}
